import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import OAuthCallbackHandler from "@/pages/OAuthCallbackHandler";
import Dashboard from "@/pages/Dashboard";
import Connections from "@/pages/Connections";
import Executor from "@/pages/Executor";
import Scheduler from "@/pages/Scheduler";
import Logs from "@/pages/Logs";
import Webhooks from "@/pages/Webhooks";
import NLInterface from "@/pages/NLInterface";
import Settings from "@/pages/Settings";
import Alerts from "@/pages/Alerts";
import Transforms from "@/pages/Transforms";
import ConnectionDetail from "@/pages/ConnectionDetail";
import QueryEngine from "@/pages/QueryEngine";
import EnvironmentMap from "@/pages/EnvironmentMap";
import MatrDashboard from "@/pages/MatrDashboard";
import SharedMapView from "@/pages/SharedMapView";
import RetentionManagement from "@/pages/RetentionManagement";
import StatusPage from "@/pages/StatusPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import ConstellationPage from "@/pages/ConstellationPage";
import SignalExchangePage from "@/pages/SignalExchangePage";
import CommonsPage from "@/pages/CommonsPage";
import HeatCheckPage from "@/pages/HeatCheckPage";
import DataDictionaryPage from "@/pages/DataDictionaryPage";
import DigestPage from "@/pages/DigestPage";
import VoicePage from "@/pages/VoicePage";

// Layout
import AppLayout from "@/components/AppLayout";
import CommandPalette from "@/components/CommandPalette";
import ErrorBoundary from "@/components/ErrorBoundary";

// Auth Context
import { AuthProvider, useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading, checkAuth } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!user && !loading) {
      checkAuth();
    }
  }, [user, loading, checkAuth]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#07090F]">
        <div className="text-center">
          <div className="font-mono text-[#0ABFA3] text-lg">
            <span className="ascii-spinner mr-2"></span>
            LOADING
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <AppLayout><ErrorBoundary>{children}</ErrorBoundary></AppLayout>;
};

// Check if current URL has a session_id (OAuth callback)
// This runs SYNCHRONOUSLY during render, before any useEffect
function hasSessionId() {
  const hash = window.location.hash || '';
  const href = window.location.href || '';
  return hash.includes('session_id=') || href.includes('session_id=');
}

// OAuth Gate — detects callback BEFORE AuthProvider mounts
// This prevents the race condition where checkAuth() fires /auth/me before session exchange
const OAuthGate = ({ children }) => {
  const location = useLocation();

  // CRITICAL: Check for session_id in URL hash BEFORE AuthProvider runs
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=') || hasSessionId()) {
    // Render the callback handler OUTSIDE of AuthProvider — no race condition possible
    return <OAuthCallbackHandler />;
  }

  // Normal app flow — AuthProvider mounts and checkAuth() runs safely
  return <AuthProvider>{children}</AuthProvider>;
};

// Main App Router (only renders when NOT in OAuth callback)
const AppRouter = () => {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/map/shared/:token" element={<SharedMapView />} />
      <Route path="/system-status" element={<StatusPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/connections" element={<ProtectedRoute><Connections /></ProtectedRoute>} />
      <Route path="/connections/:connectionId" element={<ProtectedRoute><ConnectionDetail /></ProtectedRoute>} />
      <Route path="/query" element={<ProtectedRoute><QueryEngine /></ProtectedRoute>} />
      <Route path="/map" element={<ProtectedRoute><EnvironmentMap /></ProtectedRoute>} />
      <Route path="/matr" element={<ProtectedRoute><MatrDashboard /></ProtectedRoute>} />
      <Route path="/retention" element={<ProtectedRoute><RetentionManagement /></ProtectedRoute>} />
      <Route path="/executor" element={<ProtectedRoute><Executor /></ProtectedRoute>} />
      <Route path="/executor/:connectionId" element={<ProtectedRoute><Executor /></ProtectedRoute>} />
      <Route path="/scheduler" element={<ProtectedRoute><Scheduler /></ProtectedRoute>} />
      <Route path="/logs" element={<ProtectedRoute><Logs /></ProtectedRoute>} />
      <Route path="/alerts" element={<ProtectedRoute><Alerts /></ProtectedRoute>} />
      <Route path="/transforms" element={<ProtectedRoute><Transforms /></ProtectedRoute>} />
      <Route path="/webhooks" element={<ProtectedRoute><Webhooks /></ProtectedRoute>} />
      <Route path="/nl" element={<ProtectedRoute><NLInterface /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/constellation" element={<ProtectedRoute><ConstellationPage /></ProtectedRoute>} />
      <Route path="/signals" element={<ProtectedRoute><SignalExchangePage /></ProtectedRoute>} />
      <Route path="/commons" element={<ProtectedRoute><CommonsPage /></ProtectedRoute>} />
      <Route path="/heatcheck" element={<ProtectedRoute><HeatCheckPage /></ProtectedRoute>} />
      <Route path="/data-dictionary" element={<ProtectedRoute><DataDictionaryPage /></ProtectedRoute>} />
      <Route path="/digest" element={<ProtectedRoute><DigestPage /></ProtectedRoute>} />
      <Route path="/voice" element={<ProtectedRoute><VoicePage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <OAuthGate>
        <a href="#main-content" className="skip-to-content">Skip to content</a>
        <AppRouter />
        <CommandPalette />
        <Toaster 
          position="bottom-right"
          toastOptions={{
            style: {
              background: '#0C1220',
              border: '1px solid rgba(10,191,163,0.12)',
              color: '#F0F8FF',
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: '12px',
              letterSpacing: '0.5px',
            },
          }}
        />
      </OAuthGate>
    </BrowserRouter>
  );
}

export default App;
